import { useQuery } from "@apollo/client";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles
} from "@material-ui/core";
import CancelCircleIcon from "@material-ui/icons/Cancel";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { Alert } from "@material-ui/lab";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import { VanIntegrationFields } from "src/graphql/generated/VanIntegrationFields";
import {
  vanIntegrationDetailsWithValidation,
  vanIntegrationDetailsWithValidationVariables
} from "src/graphql/generated/vanIntegrationDetailsWithValidation";
import { GET_INTEGRATION_DETAILS_WITH_VALIDATION } from "src/graphql/integrations";
import palette from "src/styles/palette";

const MappedIcon = () => <CheckCircleIcon htmlColor={palette.green} />;
const UnmappedIcon = () => <CancelCircleIcon htmlColor={palette.darkGray} />;

const useStyles = makeStyles(theme => ({
  dialogContent: {
    width: 600
  },
  fieldName: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center"
  },
  fieldLabel: {
    paddingLeft: "5px"
  },
  field: {
    marginBottom: theme.spacing(2)
  }
}));

export default function ViewIntegrationDialog({
  open,
  onClose,
  integration
}: {
  open: boolean;
  onClose: () => void;
  integration: VanIntegrationFields;
}): JSX.Element {
  const classes = useStyles();

  const { data, error, loading } = useQuery<
    vanIntegrationDetailsWithValidation,
    vanIntegrationDetailsWithValidationVariables
  >(GET_INTEGRATION_DETAILS_WITH_VALIDATION, {
    variables: {
      id: integration.id
    },
    fetchPolicy: "network-only"
  });

  function renderContent(): JSX.Element {
    if (loading) {
      return <LoadingIndicator />;
    }

    if (error) {
      if (error.graphQLErrors[0].extensions?.code === "VAN_PROXY_ERROR") {
        return <Alert severity="error">{error.graphQLErrors[0].message}</Alert>;
      }

      return <ErrorCard errors={[error]} />;
    }

    const introspectionData = data!.vanIntegration.introspectionData;
    return (
      <div>
        <div className={classes.field}>
          <strong>Name</strong>
          <br />
          <span>{introspectionData.name}</span>
        </div>
        <div className={classes.field}>
          <strong>Created At</strong>
          <br />
          <span>{new Date(introspectionData.createdAt).toLocaleString()}</span>
        </div>
        <div className={classes.field}>
          <strong>Last Updated At</strong>
          <br />
          <span>{new Date(introspectionData.updatedAt).toLocaleString()}</span>
        </div>
        <div className={classes.field}>
          <strong>API Key Application Name</strong>
          <br />
          <span>{introspectionData.applicationName}</span>
        </div>
        <div className={classes.field}>
          <strong>API Key Reference</strong>
          <br />
          <span>{introspectionData.keyReference}</span>
        </div>
        <div className={classes.field}>
          <strong>NGP VAN / EveryAction Database Name</strong>
          <br />
          <span>{introspectionData.databaseName}</span>
        </div>
        <div className={classes.field}>
          <strong>NGP VAN / EveryAction Committee Name</strong>
          <br />
          <span>{introspectionData.committeeName}</span>
        </div>
        <div className={classes.field}>
          <strong>Database Modes</strong>
          <br />
          <div className={classes.fieldName}>
            {introspectionData.hasMyCampaign ? (
              <MappedIcon />
            ) : (
              <UnmappedIcon />
            )}
            <div className={classes.fieldLabel}>My Campaign</div>
          </div>
          <div className={classes.fieldName}>
            {introspectionData.hasMyVoters ? <MappedIcon /> : <UnmappedIcon />}
            <div className={classes.fieldLabel}>My Voters</div>
          </div>
        </div>
        <div className={classes.field}>
          <strong>Scale to Win Internal ID</strong>
          <br />
          <span>{introspectionData.id}</span>
        </div>
      </div>
    );
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>Integration Details</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {renderContent()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
